// ══════════════════════════════════════════════════════════════
// COMPASS – Board Member Reducer (US-041)
// ══════════════════════════════════════════════════════════════

import { createReducer, on } from '@ngrx/store';
import { BoardMemberActions } from './board-member.actions';
import { BoardMemberState, initialBoardMemberState } from './board-member.state';
import { ManagementCompanyBoardMemberDto } from '../../api/models/management-company-board-member-dto';

/** Sorted by last then first name, the same way after a load and after a change, so rows never jump. */
function sortByName(list: ManagementCompanyBoardMemberDto[]): ManagementCompanyBoardMemberDto[] {
  return [...list].sort((a, b) =>
    (a.lastName ?? '').localeCompare(b.lastName ?? '') || (a.firstName ?? '').localeCompare(b.firstName ?? ''));
}

/** Replaces the member with the same id, or adds it. */
function upsert(list: ManagementCompanyBoardMemberDto[], member: ManagementCompanyBoardMemberDto): ManagementCompanyBoardMemberDto[] {
  return sortByName([...list.filter((m) => m.id !== member.id), member]);
}

export const boardMemberReducer = createReducer(
  initialBoardMemberState,

  on(BoardMemberActions.loadBoardMembers, (state): BoardMemberState => ({ ...state, loading: true, error: null })),
  on(BoardMemberActions.loadBoardMembersSuccess, (state, { boardMembers }): BoardMemberState => ({
    ...state, boardMembers: sortByName(boardMembers), loading: false,
  })),
  on(BoardMemberActions.loadBoardMembersFailure, (state, { error }): BoardMemberState => ({ ...state, loading: false, error })),

  on(BoardMemberActions.saveBoardMember, (state): BoardMemberState => ({ ...state, saving: true, error: null })),
  on(BoardMemberActions.saveBoardMemberSuccess, (state, { boardMember }): BoardMemberState => ({
    ...state, boardMembers: upsert(state.boardMembers, boardMember), saving: false,
  })),
  on(BoardMemberActions.saveBoardMemberFailure, (state, { error }): BoardMemberState => ({ ...state, saving: false, error })),

  on(BoardMemberActions.deactivateBoardMember, BoardMemberActions.reactivateBoardMember,
    (state): BoardMemberState => ({ ...state, updating: true, error: null })),
  on(BoardMemberActions.deactivateBoardMemberSuccess, BoardMemberActions.reactivateBoardMemberSuccess,
    (state, { boardMember }): BoardMemberState => ({
      ...state, boardMembers: upsert(state.boardMembers, boardMember), updating: false,
    })),
  on(BoardMemberActions.deactivateBoardMemberFailure, BoardMemberActions.reactivateBoardMemberFailure,
    (state, { error }): BoardMemberState => ({ ...state, updating: false, error })),

  on(BoardMemberActions.clearBoardMembers, (): BoardMemberState => initialBoardMemberState),
);
